import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import {
  onConfirmDangerMessage,
  onConfirmInfoMessage,
  onConfirmWarningMessage,
  useParamsState,
  useValidationErrors,
} from '@/helpers'
import { ROUTES_ADMIN } from '@/router'
import {
  useGeneratePurchaseOrder,
  useGeneratePurchaseOrderPreview,
  useSaleOrderVariantList,
  useSupplierDetail,
  useSupplyItemsPreview,
  useUploadSupplierOrderInvoice,
} from '@/services'
import { Button, showToast, Text } from '@mercai/clever'

import {
  DrawerAdditionalProducts,
  DrawerSupplier,
  GeneralInformation,
  ShowTotal,
} from '../../components'

import {
  DrawerSaleOrders,
  GenerateGeneralInformation,
  ModalGeneratePurchaseOrder,
  SelectAdditionalSupplierProducts,
  SelectSupplierOrderItems,
} from './components'

import {
  GeneratePurchaseOrderPayloadSupplyItemProps,
  SaleOrderProps,
} from '@/types'

import {
  formattedPayloadToGeneratePurchaseOrder,
  formattedPayloadToGeneratePurchaseOrderPreview,
  GeneratePurchaseOrderValues,
} from '../../helpers'
import {
  hasSaleOrderItemCompletion,
  schema,
  schemaPO,
} from './generate-purchase-order.constants'
import styles from './generate-purchase-order.module.css'

export const GeneratePurchaseOrder = () => {
  const { t } = useTranslation('screenSupplierOrderManagementComponents')

  const navigate = useNavigate()

  const [queryParams, setQueryParams] = useParamsState({
    form: '',
  })

  const [loading, setLoading] = useState(false)

  const [values, setValues] = useState<GeneratePurchaseOrderValues>({
    purchaseOrderCode: '',
    currency: 'MXN',
  })

  const [saleOrdersToPayload, setSaleOrdersToPayload] = useState<
    Record<
      string,
      Record<string, GeneratePurchaseOrderPayloadSupplyItemProps[]>
    >
  >({})

  const [isOpenPreview, setisOpenPreview] = useState(false)

  const valuesToPayload = useMemo(() => {
    return formattedPayloadToGeneratePurchaseOrder(values, saleOrdersToPayload)
  }, [values, saleOrdersToPayload])

  const valuesToPreview = useMemo(() => {
    return {
      ...formattedPayloadToGeneratePurchaseOrderPreview(
        values,
        saleOrdersToPayload,
      ),
    }
  }, [values?.additionalProducts, values?.currency, saleOrdersToPayload])

  const isGeneratePurchaseOrder = useMemo(() => {
    const itemsToGeneratePO = Object.values(saleOrdersToPayload).reduce(
      (acc, order) =>
        acc.concat(
          ...Object.values(order)
            .reduce((a, items) => a.concat(items), [])
            .filter((item) => item.generatePo === true),
        ),
      [] as GeneratePurchaseOrderPayloadSupplyItemProps[],
    )
    return (
      (values?.additionalProducts?.length || 0) > 0 ||
      (itemsToGeneratePO && itemsToGeneratePO?.length > 0)
    )
  }, [values, saleOrdersToPayload])

  const { isValid, errors, onHandleListenErrors } = useValidationErrors(
    isGeneratePurchaseOrder ? schemaPO(t) : schema(t),
    {
      purchaseOrderCode: values?.purchaseOrderCode,
      supplierUuid: values?.supplier?.uuid,
      items: [
        ...(valuesToPayload.additionalProducts || []),
        ...(valuesToPayload.saleOrders?.[0]?.items?.[0]?.supplyItems || []),
      ],
    },
  )

  const { data: PreviewPO, isLoading: isLoadingPreviewPO } =
    useGeneratePurchaseOrderPreview(
      valuesToPreview,
      !!(
        (valuesToPreview?.saleOrders &&
          valuesToPreview?.saleOrders?.length > 0) ||
        (valuesToPreview?.additionalProducts &&
          valuesToPreview?.additionalProducts?.length > 0)
      ),
    )

  const { data: previewSupplyItems, isLoading: isLoadingPreviewSupplyItems } =
    useSupplyItemsPreview(
      { saleOrders: values?.saleOrders?.map((so) => so.reference) || [] },
      !!(values?.saleOrders && values?.saleOrders?.length > 0),
    )

  const { data: supplierDetail } = useSupplierDetail(queryParams?.supplierUuid)
  const { data: orderCenterSaleOrderList } = useSaleOrderVariantList({
    reference: queryParams?.references,
    variantFields: 'simplifiedList',
  })

  const { mutateAsync: createPurchaseOrder, isPending: isLoadingCreatePO } =
    useGeneratePurchaseOrder()

  const { mutateAsync: uploadSupplierOrderInvoice } =
    useUploadSupplierOrderInvoice()

  const isLoadingPO = isLoadingCreatePO

  const handleOnChangeSaleOrders = (saleOrders: SaleOrderProps[]) => {
    setValues((prev) => ({
      ...prev,
      saleOrders,
    }))

    setSaleOrdersToPayload((prevPayload) => {
      const selectedUuids = new Set(saleOrders.map((so) => so.uuid))
      const newPayload: typeof prevPayload = {}
      for (const uuid of Object.keys(prevPayload)) {
        if (selectedUuids.has(uuid)) {
          newPayload[uuid] = prevPayload[uuid]
        }
      }
      return newPayload
    })
  }

  const closeDrawer = () => {
    setQueryParams({ ...queryParams, form: '' })
  }

  const onDropFile = (
    files: File[],
    key: 'invoicePDFFile' | 'invoiceXMLFile',
  ) => {
    const file = files[0]
    if (file) {
      setValues((prev) => ({
        ...prev,
        [key]: file,
      }))
    }
  }

  const onCancelEdit = async () => {
    const confirm = await onConfirmDangerMessage(
      t('generatePurchaseOrderCreateConfirmTitle'),
      t('generatePurchaseOrderCreateConfirmCancelMessage'),
      t('generatePurchaseOrderCreateConfirmButtonCreate'),
      t('generatePurchaseOrderCreateConfirmButtonCancel'),
    )

    if (!confirm) {
      return
    }

    return navigate(
      ROUTES_ADMIN.SUPPLY.ORDER_MANAGEMENT.GENERATE_PURCHASE_ORDER.fullPath(),
    )
  }

  const onHandleOpenPreview = () => {
    if (!isValid) {
      onHandleListenErrors(true)
      return
    }

    onHandleListenErrors(false)

    setisOpenPreview(true)
  }

  const onHandleSubmit = async (sendPo?: boolean) => {
    if (!isValid) {
      onHandleListenErrors(true)
      return
    }

    onHandleListenErrors(false)

    const hasSaleOrderItemCompletionValue = hasSaleOrderItemCompletion(
      values,
      saleOrdersToPayload,
    )

    if (!hasSaleOrderItemCompletionValue) {
      const confirm = await onConfirmWarningMessage(
        t('generatePurchaseOrderCreateConfirmTitle'),
        isGeneratePurchaseOrder
          ? t('generatePurchaseOrderCreateMissingQuantityConfirmMessage')
          : t('generatePurchaseOrderAssociateMissingQuantityConfirmMessage'),
        t('generatePurchaseOrderCreateConfirmButtonCreate'),
        t('generatePurchaseOrderCreateConfirmButtonCancel'),
      )

      if (!confirm) {
        return
      }
    } else {
      const confirm = await onConfirmInfoMessage(
        t('generatePurchaseOrderCreateConfirmTitle'),
        isGeneratePurchaseOrder
          ? t('generatePurchaseOrderCreateConfirmMessage')
          : t('generatePurchaseOrderAssociateConfirmMessage'),
        t('generatePurchaseOrderCreateConfirmButtonCreate'),
        t('generatePurchaseOrderCreateConfirmButtonCancel'),
      )

      if (!confirm) {
        return
      }
    }

    setLoading(true)

    const payload = {
      ...valuesToPayload,
      sendPo,
    }

    const response = await createPurchaseOrder(payload)

    if (!response.success) {
      showToast({
        variant: 'error',
        title: response?.message
          ? response.message
          : t('generatePurchaseOrderCreateErrorMessage'),
        position: 'bottom-right',
      })

      setLoading(false)

      return
    }

    if (
      isGeneratePurchaseOrder &&
      (values?.invoiceXMLFile || values?.invoicePDFFile)
    ) {
      if (values?.invoicePDFFile && response?.data?.invoiceUuid) {
        await uploadSupplierOrderInvoice({
          uuid: response?.data?.invoiceUuid,
          file: values.invoicePDFFile,
          type: 'pdf',
        })
      }

      if (values?.invoiceXMLFile && response?.data?.invoiceUuid) {
        await uploadSupplierOrderInvoice({
          uuid: response?.data?.invoiceUuid,
          file: values.invoiceXMLFile,
          type: 'xml',
        })
      }
    }

    setLoading(false)

    showToast({
      variant: 'success',
      title: t('generatePurchaseOrderCreateSuccessMessage'),
      position: 'bottom-right',
    })

    navigate(
      `${ROUTES_ADMIN.SUPPLY.ORDER_MANAGEMENT.PURCHASE_ORDER.fullPath()}?search=${response?.data?.reference}`,
    )
  }

  const onChangeCurrency = (currency: 'USD' | 'MXN') => {
    setValues((prev) => ({
      ...prev,
      currency,
      additionalProducts:
        currency === prev.currency
          ? prev.additionalProducts
          : prev?.additionalProducts?.map((product) => ({
              ...product,
              cost:
                currency === 'MXN'
                  ? String(
                      +(product.cost || 0) *
                        +(PreviewPO?.data?.dollarExchangeRate || 1),
                    )
                  : String(
                      +(product.cost || 0) /
                        +(PreviewPO?.data?.dollarExchangeRate || 1),
                    ),
            })),
    }))
  }

  useEffect(() => {
    if (supplierDetail?.data && orderCenterSaleOrderList?.data?.results) {
      setValues((prev) => ({
        ...prev,
        supplier: supplierDetail.data,
        saleOrders: orderCenterSaleOrderList?.data?.results || [],
      }))
    }
  }, [supplierDetail, orderCenterSaleOrderList])

  useEffect(() => {
    setSaleOrdersToPayload((prevPayload) => {
      const newPayload: Record<
        string,
        Record<string, GeneratePurchaseOrderPayloadSupplyItemProps[]>
      > = { ...prevPayload }

      previewSupplyItems?.data?.saleOrders?.forEach((saleOrder) => {
        const saleOrderUuid = saleOrder?.uuid || ''

        if (!newPayload[saleOrderUuid]) {
          newPayload[saleOrderUuid] = {}
        }

        saleOrder?.items?.forEach((item) => {
          if (!newPayload[saleOrderUuid][item?.uuid || '']) {
            newPayload[saleOrderUuid][item?.uuid || ''] = []
          }

          const valueToGeneratePO = newPayload?.[saleOrderUuid]?.[
            item?.uuid || ''
          ]?.find((supplyItem) => supplyItem?.generatePo)

          if (
            !valueToGeneratePO &&
            newPayload[saleOrderUuid][item?.uuid || ''].length <= 0
          ) {
            newPayload[saleOrderUuid][item?.uuid || ''].push({
              generatePo: true,
              quantity: item?.missingQuantity || 0,
            })
          }
        })
      })

      return newPayload
    })
  }, [previewSupplyItems])

  useEffect(() => {
    const mainElement = document.querySelector('main')
    if (mainElement) {
      ;(mainElement as HTMLElement).style.position = 'relative'
    }
    return () => {
      if (mainElement) {
        ;(mainElement as HTMLElement).style.position = ''
      }
    }
  }, [])

  return (
    <div className={styles.container}>
      <div className={styles.core}>
        <header className={styles.header}>
          <Text variant="text-small" className={styles.breadcrumb}>
            <Link
              to={''}
              onClick={onCancelEdit}
            >{`${t('generatePurchaseOrderBreadcum')}`}</Link>
            {` / `}
            <span>PO</span>
          </Text>

          <div className={styles['header-content']}>
            <div>
              <Text variant="title-large">
                {t('generatePurchaseOrderCreateTitle')}
              </Text>
            </div>
          </div>
        </header>

        <GeneralInformation
          values={values}
          setValues={setValues}
          onShowAddSupplier={() =>
            setQueryParams({ ...queryParams, form: 'supplier' })
          }
          errors={errors}
          isQuotation
          isLoading={isLoadingPreviewPO}
          position={1}
        />

        <SelectSupplierOrderItems
          values={values}
          setValues={setValues}
          onChangeCurrency={onChangeCurrency}
          saleOrdersToPayload={saleOrdersToPayload}
          setSaleOrdersToPayload={setSaleOrdersToPayload}
          disabled={!values?.supplier}
          onShowAddSaleOrders={() =>
            setQueryParams({ ...queryParams, form: 'sale-orders' })
          }
          preview={previewSupplyItems?.data}
          isLoading={isLoadingPreviewSupplyItems}
          errors={errors}
          position={2}
        />

        <SelectAdditionalSupplierProducts
          values={values}
          setValues={setValues}
          onChangeCurrency={onChangeCurrency}
          disabled={!values?.supplier}
          onShowAddAdditionalProducts={() =>
            setQueryParams({ ...queryParams, form: 'select-supplier-products' })
          }
          preview={PreviewPO?.data}
          isLoading={isLoadingPreviewPO}
          errors={errors}
          position={3}
        />

        {errors?.items ? (
          <Text className={styles.error}>{errors.items as string}</Text>
        ) : null}

        {isGeneratePurchaseOrder && (
          <ShowTotal
            total={PreviewPO?.data?.total || 0}
            currency={PreviewPO?.data?.currency || 'MXN'}
            subtotal={PreviewPO?.data?.totalWithoutIva || 0}
            iva={PreviewPO?.data?.iva || 0}
            dollarExchangeRate={PreviewPO?.data?.dollarExchangeRate || 0}
            isLoading={isLoadingPreviewPO}
            position={4}
          />
        )}

        {isGeneratePurchaseOrder && (
          <GenerateGeneralInformation
            values={values}
            setValues={setValues}
            onDropFile={onDropFile}
            position={5}
            errors={errors}
            isQuotation
          />
        )}
      </div>
      <div className={styles.footer}>
        <div className={styles.actions}>
          <Button variant="secondary" onClick={onCancelEdit}>
            {t('generatePurchaseOrderCancelButton')}
          </Button>

          <Button variant="secondary" onClick={onHandleOpenPreview}>
            {t('generatePurchaseOrderPreviewButton')}
          </Button>

          <Button
            variant="primary"
            onClick={() => onHandleSubmit(false)}
            disabled={isLoadingPO || loading}
            isActive={!isLoadingPO && !loading}
            isLoading={isLoadingPO || loading}
          >
            {t('generatePurchaseOrderConfirmButton')}
          </Button>

          <Button
            variant="primary"
            onClick={() => onHandleSubmit(true)}
            disabled={isLoadingPO || loading}
            isActive={!isLoadingPO && !loading}
            isLoading={isLoadingPO || loading}
          >
            {t('generatePurchaseOrderConfirmAndSendButton')}
          </Button>
        </div>
      </div>

      <DrawerSupplier
        supplier={values?.supplier}
        setSupplier={(sup) => setValues({ ...values, supplier: sup })}
        isOpen={queryParams.form === 'supplier'}
        onClose={closeDrawer}
      />

      <DrawerSaleOrders
        supplierUuid={values?.supplier?.uuid}
        saleOrders={values?.saleOrders}
        setSaleOrders={handleOnChangeSaleOrders}
        isOpen={queryParams.form === 'sale-orders'}
        onClose={closeDrawer}
      />

      <DrawerAdditionalProducts
        currency={values?.currency || 'MXN'}
        supplierUuid={values?.supplier?.uuid}
        additionalProducts={values?.additionalProducts}
        setAdditionalProducts={(val) =>
          setValues({ ...values, additionalProducts: val })
        }
        isOpen={queryParams.form === 'select-supplier-products'}
        onClose={closeDrawer}
      />

      <ModalGeneratePurchaseOrder
        isOpen={isOpenPreview}
        values={valuesToPayload}
        onSubmit={onHandleSubmit}
        setIsOpen={setisOpenPreview}
        isLoading={loading}
      />
    </div>
  )
}
